
import React from 'react';
import * as LabelPrimitive from "@radix-ui/react-label";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import * as AlertDialogPrimitive from "@radix-ui/react-alert-dialog";
import * as AvatarPrimitive from "@radix-ui/react-avatar";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import * as PopoverPrimitive from "@radix-ui/react-popover";
import * as SelectPrimitive from "@radix-ui/react-select";
import * as TabsPrimitive from "@radix-ui/react-tabs";
import * as DropdownMenuPrimitive from "@radix-ui/react-dropdown-menu";
import * as ScrollAreaPrimitive from "@radix-ui/react-scroll-area";

// Label wrapper component
export const EnhancedLabel = React.forwardRef<
  React.ElementRef<typeof LabelPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof LabelPrimitive.Root> & { children: React.ReactNode }
>(({ className, children, ...props }, ref) => (
  <LabelPrimitive.Root
    ref={ref}
    className={className}
    {...props}
  >
    {children}
  </LabelPrimitive.Root>
));
EnhancedLabel.displayName = "EnhancedLabel";

// Dialog wrappers
export const EnhancedDialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title> & { children: React.ReactNode }
>(({ className, children, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={className}
    {...props}
  >
    {children}
  </DialogPrimitive.Title>
));
EnhancedDialogTitle.displayName = "EnhancedDialogTitle";

export const EnhancedDialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description> & { children: React.ReactNode }
>(({ className, children, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={className}
    {...props}
  >
    {children}
  </DialogPrimitive.Description>
));
EnhancedDialogDescription.displayName = "EnhancedDialogDescription";

export const EnhancedDialogTrigger = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Trigger> & { children: React.ReactNode }
>(({ children, ...props }, ref) => (
  <DialogPrimitive.Trigger ref={ref} {...props}>
    {children}
  </DialogPrimitive.Trigger>
));
EnhancedDialogTrigger.displayName = "EnhancedDialogTrigger";

const DialogPortal = DialogPrimitive.Portal;

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    className={`fixed inset-0 z-50 bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 ${className || ''}`}
    {...props}
    ref={ref}
  />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

export const EnhancedDialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & { children: React.ReactNode }
>(({ className, children, ...props }, ref) => (
  <DialogPortal>
    <DialogOverlay />
    <DialogPrimitive.Content
      ref={ref}
      className={`fixed left-[50%] top-[50%] z-50 grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 border bg-background p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg ${className || ''}`}
      {...props}
    >
      {children}
    </DialogPrimitive.Content>
  </DialogPortal>
));
EnhancedDialogContent.displayName = "EnhancedDialogContent";

// Alert dialog wrappers
export const EnhancedAlertDialogTitle = React.forwardRef<
  React.ElementRef<typeof AlertDialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Title> & { children: React.ReactNode }
>(({ className, children, ...props }, ref) => (
  <AlertDialogPrimitive.Title
    ref={ref}
    className={`text-lg font-semibold ${className || ''}`}
    {...props}
  >
    {children}
  </AlertDialogPrimitive.Title>
));
EnhancedAlertDialogTitle.displayName = "EnhancedAlertDialogTitle";

export const EnhancedAlertDialogDescription = React.forwardRef<
  React.ElementRef<typeof AlertDialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Description> & { children: React.ReactNode }
>(({ className, children, ...props }, ref) => (
  <AlertDialogPrimitive.Description
    ref={ref}
    className={`text-sm text-muted-foreground ${className || ''}`}
    {...props}
  >
    {children}
  </AlertDialogPrimitive.Description>
));
EnhancedAlertDialogDescription.displayName = "EnhancedAlertDialogDescription";

export const EnhancedAlertDialogAction = React.forwardRef<
  React.ElementRef<typeof AlertDialogPrimitive.Action>,
  React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Action> & { 
    children: React.ReactNode;
    onClick?: () => void;
  }
>(({ className, children, onClick, ...props }, ref) => (
  <AlertDialogPrimitive.Action
    ref={ref}
    className={`inline-flex h-10 px-4 py-2 items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 bg-primary text-primary-foreground hover:bg-primary/90 ${className || ''}`}
    {...props}
    onClick={onClick}
  >
    {children}
  </AlertDialogPrimitive.Action>
));
EnhancedAlertDialogAction.displayName = "EnhancedAlertDialogAction";

export const EnhancedAlertDialogCancel = React.forwardRef<
  React.ElementRef<typeof AlertDialogPrimitive.Cancel>,
  React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Cancel> & { 
    children: React.ReactNode;
    onClick?: () => void;
  }
>(({ className, children, onClick, ...props }, ref) => (
  <AlertDialogPrimitive.Cancel
    ref={ref}
    className={`inline-flex h-10 px-4 py-2 items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 border border-input bg-background hover:bg-accent hover:text-accent-foreground mt-2 sm:mt-0 ${className || ''}`}
    {...props}
    onClick={onClick}
  >
    {children}
  </AlertDialogPrimitive.Cancel>
));
EnhancedAlertDialogCancel.displayName = "EnhancedAlertDialogCancel";

export const EnhancedAlertDialogTrigger = React.forwardRef<
  React.ElementRef<typeof AlertDialogPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Trigger> & { children: React.ReactNode }
>(({ children, ...props }, ref) => (
  <AlertDialogPrimitive.Trigger ref={ref} {...props}>
    {children}
  </AlertDialogPrimitive.Trigger>
));
EnhancedAlertDialogTrigger.displayName = "EnhancedAlertDialogTrigger";

const AlertDialogPortal = AlertDialogPrimitive.Portal;

const AlertDialogOverlay = React.forwardRef<
  React.ElementRef<typeof AlertDialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <AlertDialogPrimitive.Overlay
    className={`fixed inset-0 z-50 bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 ${className || ''}`}
    {...props}
    ref={ref}
  />
));
AlertDialogOverlay.displayName = AlertDialogPrimitive.Overlay.displayName;

export const EnhancedAlertDialogContent = React.forwardRef<
  React.ElementRef<typeof AlertDialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Content> & { children: React.ReactNode }
>(({ children, className, ...props }, ref) => (
  <AlertDialogPortal>
    <AlertDialogOverlay />
    <AlertDialogPrimitive.Content 
      ref={ref}
      className={`fixed left-[50%] top-[50%] z-50 grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 border bg-background p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg ${className || ''}`}
      {...props}
    >
      {children}
    </AlertDialogPrimitive.Content>
  </AlertDialogPortal>
));
EnhancedAlertDialogContent.displayName = "EnhancedAlertDialogContent";

// Avatar wrappers
export const EnhancedAvatar = React.forwardRef<
  React.ElementRef<typeof AvatarPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Root> & { children: React.ReactNode }
>(({ className, children, ...props }, ref) => (
  <AvatarPrimitive.Root
    ref={ref}
    className={className}
    {...props}
  >
    {children}
  </AvatarPrimitive.Root>
));
EnhancedAvatar.displayName = "EnhancedAvatar";

export const EnhancedAvatarImage = React.forwardRef<
  React.ElementRef<typeof AvatarPrimitive.Image>,
  React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Image> & { 
    src: string;
    alt?: string;
    className?: string;
    onLoad?: () => void;
    onError?: () => void; 
  }
>(({ className, src, alt, onLoad, onError, ...props }, ref) => (
  <AvatarPrimitive.Image
    ref={ref}
    className={className}
    src={src}
    alt={alt}
    onLoadingStatusChange={(status) => {
      if (status === 'loaded') onLoad?.();
      if (status === 'error') onError?.();
    }}
    {...props}
  />
));
EnhancedAvatarImage.displayName = "EnhancedAvatarImage";

export const EnhancedAvatarFallback = React.forwardRef<
  React.ElementRef<typeof AvatarPrimitive.Fallback>,
  React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Fallback> & { children: React.ReactNode }
>(({ className, children, ...props }, ref) => (
  <AvatarPrimitive.Fallback
    ref={ref}
    className={className}
    {...props}
  >
    {children}
  </AvatarPrimitive.Fallback>
));
EnhancedAvatarFallback.displayName = "EnhancedAvatarFallback";

// Tooltip wrappers
export const EnhancedTooltipTrigger = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Trigger> & { 
    children: React.ReactNode;
    asChild?: boolean;
  }
>(({ children, ...props }, ref) => (
  <TooltipPrimitive.Trigger ref={ref} {...props}>
    {children}
  </TooltipPrimitive.Trigger>
));
EnhancedTooltipTrigger.displayName = "EnhancedTooltipTrigger";

// Popover wrappers
export const EnhancedPopoverTrigger = React.forwardRef<
  React.ElementRef<typeof PopoverPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof PopoverPrimitive.Trigger> & { 
    children: React.ReactNode;
    asChild?: boolean;
  }
>(({ children, ...props }, ref) => (
  <PopoverPrimitive.Trigger ref={ref} {...props}>
    {children}
  </PopoverPrimitive.Trigger>
));
EnhancedPopoverTrigger.displayName = "EnhancedPopoverTrigger";

// Select wrappers
export const EnhancedSelectTrigger = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger> & { children: React.ReactNode }
>(({ className, children, ...props }, ref) => (
  <SelectPrimitive.Trigger
    ref={ref}
    className={className}
    {...props}
  >
    {children}
  </SelectPrimitive.Trigger>
));
EnhancedSelectTrigger.displayName = "EnhancedSelectTrigger";

export const EnhancedSelectContent = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Content> & { children: React.ReactNode }
>(({ className, children, ...props }, ref) => (
  <SelectPrimitive.Content
    ref={ref}
    className={className}
    {...props}
  >
    {children}
  </SelectPrimitive.Content>
));
EnhancedSelectContent.displayName = "EnhancedSelectContent";

export const EnhancedSelectItem = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Item> & { children: React.ReactNode }
>(({ className, children, ...props }, ref) => (
  <SelectPrimitive.Item
    ref={ref}
    className={className}
    {...props}
  >
    {children}
  </SelectPrimitive.Item>
));
EnhancedSelectItem.displayName = "EnhancedSelectItem";

// Dropdown menu wrappers
export const EnhancedDropdownMenuTrigger = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Trigger> & { 
    children: React.ReactNode;
    asChild?: boolean;
  }
>(({ children, ...props }, ref) => (
  <DropdownMenuPrimitive.Trigger ref={ref} {...props}>
    {children}
  </DropdownMenuPrimitive.Trigger>
));
EnhancedDropdownMenuTrigger.displayName = "EnhancedDropdownMenuTrigger";

export const EnhancedDropdownMenuItem = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Item> & { 
    children: React.ReactNode;
    onClick?: () => void;
  }
>(({ className, children, onClick, ...props }, ref) => (
  <DropdownMenuPrimitive.Item
    ref={ref}
    className={className}
    onClick={onClick}
    {...props}
  >
    {children}
  </DropdownMenuPrimitive.Item>
));
EnhancedDropdownMenuItem.displayName = "EnhancedDropdownMenuItem";

export const EnhancedDropdownMenuLabel = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Label>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Label> & { children: React.ReactNode }
>(({ className, children, ...props }, ref) => (
  <DropdownMenuPrimitive.Label
    ref={ref}
    className={className}
    {...props}
  >
    {children}
  </DropdownMenuPrimitive.Label>
));
EnhancedDropdownMenuLabel.displayName = "EnhancedDropdownMenuLabel";

// ScrollArea wrapper
export const EnhancedScrollArea = React.forwardRef<
  React.ElementRef<typeof ScrollAreaPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof ScrollAreaPrimitive.Root> & { children: React.ReactNode }
>(({ className, children, ...props }, ref) => (
  <ScrollAreaPrimitive.Root
    ref={ref}
    className={className}
    {...props}
  >
    {children}
  </ScrollAreaPrimitive.Root>
));
EnhancedScrollArea.displayName = "EnhancedScrollArea";

// Tabs wrappers
export const EnhancedTabs = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Root> & { children: React.ReactNode }
>(({ children, ...props }, ref) => (
  <TabsPrimitive.Root ref={ref} {...props}>
    {children}
  </TabsPrimitive.Root>
));
EnhancedTabs.displayName = "EnhancedTabs";

export const EnhancedTabsList = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.List>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.List> & { children: React.ReactNode }
>(({ className, children, ...props }, ref) => (
  <TabsPrimitive.List
    ref={ref}
    className={className}
    {...props}
  >
    {children}
  </TabsPrimitive.List>
));
EnhancedTabsList.displayName = "EnhancedTabsList";

export const EnhancedTabsTrigger = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger> & { children: React.ReactNode }
>(({ className, children, ...props }, ref) => (
  <TabsPrimitive.Trigger
    ref={ref}
    className={className}
    {...props}
  >
    {children}
  </TabsPrimitive.Trigger>
));
EnhancedTabsTrigger.displayName = "EnhancedTabsTrigger";

export const EnhancedTabsContent = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content> & { children: React.ReactNode }
>(({ className, children, ...props }, ref) => (
  <TabsPrimitive.Content
    ref={ref}
    className={className}
    {...props}
  >
    {children}
  </TabsPrimitive.Content>
));
EnhancedTabsContent.displayName = "EnhancedTabsContent";
